import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import MarkdownsView from './MarkdownsView'

const API = import.meta.env.VITE_API_URL ?? ''

interface Location { id: string; name: string }

// Price Drops — individual rival SKUs whose current menu price sits below their
// regular price. This screen was split out of the old Promotions Tracker: the
// banner-level "Promotions" view was retired (marketing text only, not detailed
// enough to be useful), leaving just the product-level markdowns view.
export default function PriceDrops() {
  const navigate = useNavigate()
  const authFetch = useAuthFetch()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.error || `HTTP ${r.status}`) })
        return r.json()
      })
      .then(d => {
        const locs: Location[] = d.locations || []
        setLocations(locs)
        if (locs.length > 0) {
          const saved = localStorage.getItem('promotions_location')
          const match = saved && locs.find(l => l.id === saved)
          setSelectedLocation(match ? saved : locs[0].id)
        }
      })
      .catch(e => setFetchError(`Locations: ${e?.message || 'Network error'}`))
  }, [authFetch])

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden', position:'relative' }}>

      {/* Topbar */}
      <div style={{ padding:'16px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexShrink:0, background:'var(--surface)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:14, minWidth:0 }}>
          <button
            onClick={() => navigate('/today')}
            style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:8, border:'1px solid var(--border-2)', background:'var(--surface-2)', color:'var(--text-2)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'var(--sans)', flexShrink:0 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-2)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <div style={{ minWidth:0 }}>
            <div data-testid="pricedrops-title" style={{ fontFamily:'var(--display)', fontSize:26, fontWeight:400, color:'var(--text-1)', letterSpacing:'-0.015em', lineHeight:1 }}>Price Drops</div>
            <div style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--text-3)', letterSpacing:'0.12em', textTransform:'uppercase', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {locations.find(l => l.id === selectedLocation)?.name ?? '—'} · Rival product markdowns
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <button
            onClick={() => navigate('/prices')}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:10, border:'none', background:'var(--accent)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'var(--sans)', boxShadow:'var(--btn-shadow, 0 4px 18px rgba(9,161,161,0.32))' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            Price matrix →
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
        {fetchError ? (
          <div style={{ padding:40, textAlign:'center' }}>
            <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--danger)', marginBottom:8 }}>Failed to load locations</div>
            <div style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--text-3)', background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:6, padding:'8px 14px', display:'inline-block', maxWidth:480 }}>{fetchError}</div>
          </div>
        ) : (
          <MarkdownsView
            locations={locations}
            selectedLocation={selectedLocation}
            onSelectLocation={setSelectedLocation}
            authFetch={authFetch}
          />
        )}
      </div>
    </div>
  )
}
